// Settings → Connections as words and rules: a row's one line ("GitLab ·
// gitlab.corvusresearch.ai · as stanley · 41 tools · Everyone"), its status
// word, the form a connector needs (a host, then a sign-in or a key), a
// refusal in one line, and what a connect answer says. Pure:
// connectionsModel.selftest.ts (runs with `npm run check:format`).

import type { CatalogueEntry, Capability, ConnectOut, Connection } from "@/api/connections";

import { isHttpsUrl } from "./signIn";

export type StatusTone = "success" | "warning" | "danger" | "muted";

const STATUS: Record<string, [string, StatusTone]> = {
  ok: ["Connected", "success"],
  needs_sign_in: ["Needs sign-in", "warning"],
  failing: ["Not working", "danger"],
  unchecked: ["Not checked yet", "muted"],
  paused: ["Paused", "muted"],
};

/** A sign-in that works but lacks access a tool asked for (not while it waits for a sign-in or is paused). */
function wantsMore(status: string | null | undefined, needsScopes?: readonly string[] | null): boolean {
  return !!needsScopes?.length && status !== "needs_sign_in" && status !== "paused";
}

/** The status in a word; a key the product refused reads so (there is nothing to sign in to). */
export function statusWords(status: string | null | undefined, authKind?: string | null, needsScopes?: readonly string[] | null): string {
  if (status === "needs_sign_in" && authKind === "api_key") return "Key refused";
  if (wantsMore(status, needsScopes)) return "Needs more access";
  return STATUS[status ?? ""]?.[0] ?? (status ? status.replace(/_/g, " ") : "Unknown");
}

/** Sign in (again) is offered only where there is a sign-in: an OAuth connection. */
export function canSignIn(c: Pick<Connection, "auth_kind">): boolean {
  return c.auth_kind === "oauth2";
}

/** What a refused key needs: a new one, changed in place from the row's menu. */
export const KEY_REFUSED_LINE = "Change the key: … → Change key.";

export function statusTone(status: string | null | undefined, needsScopes?: readonly string[] | null): StatusTone {
  if (wantsMore(status, needsScopes)) return "warning";
  return STATUS[status ?? ""]?.[1] ?? "muted";
}

/** The row's line when a tool asked for access the sign-in (or the key) lacks; "" otherwise. A key row has no sign-in to widen: its key is changed. */
export function needsScopesLine(c: Pick<Connection, "status" | "needs_scopes" | "auth_kind">): string {
  if (!wantsMore(c.status, c.needs_scopes)) return "";
  return `A tool needs more access (${(c.needs_scopes ?? []).join(", ")}): … → ${c.auth_kind === "api_key" ? "Change key" : "Allow more access"}.`;
}

const SOURCE: Record<string, string> = {
  openapi: "Its OpenAPI description",
  docs: "Drafted from its docs",
  edited: "Edited by hand",
};

/** Where the tools came from, for Details; "" for the catalogue's own. */
export function toolsSourceWords(kind: string | null | undefined): string {
  return SOURCE[kind ?? ""] ?? "";
}

/** "https://gitlab.corvusresearch.ai/api/v4/mcp" → "gitlab.corvusresearch.ai"; a bare host as typed. */
export function hostOf(address: string | null | undefined): string {
  if (!address) return "";
  try {
    return new URL(address).hostname || address;
  } catch {
    return address.replace(/^[a-z]+:\/\//i, "").split("/")[0] ?? address;
  }
}

export function instanceLabel(c: Pick<Connection, "instance" | "instance_label">): string {
  const i = c.instance ?? {};
  return hostOf(c.instance_label || i.host || i.url || i.base_url || "");
}

/** "as stanley" (the account's subject, else its label); nothing for a key or no account. */
export function accountLabel(c: Pick<Connection, "account">): string {
  const a = c.account;
  if (!a || a.kind === "none" || a.kind === "key") return "";
  const who = (a.subject || a.label || "").trim();
  return who ? `as ${who}` : "";
}

export function toolsLabel(c: Pick<Connection, "tool_count" | "tool_names">): string {
  const n = c.tool_count ?? c.tool_names?.length ?? 0;
  return n === 1 ? "1 tool" : n > 1 ? `${n} tools` : "no tools yet";
}

/** Who may use it, in the operator's words: "All agents" ("*", plus any subagent named beside it), the names, or "Nobody". */
export function whoLabel(agents: readonly string[], nameOf: (template: string) => string = (t) => t): string {
  const named = agents.filter((a) => a !== "*").map(nameOf);
  if (agents.includes("*")) return ["All agents", ...named].join(", ");
  return named.length ? named.join(", ") : "Nobody";
}

/** The row's line: name, instance, account, tools, who; the status is its own word beside it. */
export function rowLine(c: Connection, nameOf?: (template: string) => string): string {
  return [c.name || c.id, instanceLabel(c), accountLabel(c), toolsLabel(c), whoLabel(c.agents ?? [], nameOf)].filter(Boolean).join(" · ");
}

export type AuthKind = "oauth2" | "api_key" | "none";

export type AddressKey = "host" | "url" | "base_url";

export interface ConnectorForm {
  /** The instance field: a self-managed product's host (with its default), or a server's or an API's address. */
  address: { key: AddressKey; label: string; default: string; placeholder: string } | null;
  /** The one way in: a sign-in button, a key field, or nothing. */
  auth: AuthKind;
  /** The other way in the operator may switch to (a sign-in or a token), when both are offered. */
  other: AuthKind | null;
  /** The key may be left empty: the product also connects with none. */
  keyOptional: boolean;
  make_at: string | null;
  needs: string | null;
}

const ADDRESS_LABEL: Record<AddressKey, string> = { host: "Host", url: "Address", base_url: "Address" };

function asAuth(kind: string | undefined): AuthKind | null {
  return kind === "oauth2" || kind === "api_key" || kind === "none" ? kind : null;
}

/**
 * The fields a connector shows. The sign-in offered is the first of the
 * connector's kinds the instance supports (the probe's `offers`, when a
 * host was probed), else the connector's first kind; `prefer` picks the
 * other one when both are offered. A sign-in counts only where the server
 * lets Corvus register itself (`dcr` or `cimd`): elsewhere it always fails,
 * so the key is offered instead, unless the operator may bring their own
 * app there (`needs_client`, which the probe answers INSTEAD of listing the
 * sign-in in `offers`: connectionsSetup.setupFor asks for the app's client).
 */
export function formFor(connector: CatalogueEntry, capability: Capability | null = null, prefer: AuthKind | null = null): ConnectorForm {
  const kinds = (connector.auth_kinds ?? []).map(asAuth).filter((k): k is AuthKind => k !== null);
  const listed = [...(capability?.offers ?? []), ...(capability?.needs_client ? ["oauth2"] : [])];
  const probed = listed.length > 0;
  const signInWorks = !probed || !!capability?.dcr || !!capability?.cimd || !!capability?.needs_client;
  const offered = listed.map(asAuth).filter((k): k is AuthKind => k !== null && (k !== "oauth2" || signInWorks));
  const usable = probed ? kinds.filter((k) => offered.includes(k)) : kinds;
  const auth = (prefer && usable.includes(prefer) ? prefer : usable[0]) ?? kinds.find((k) => k !== "oauth2") ?? kinds[0] ?? "none";
  const other = auth === "oauth2" && usable.includes("api_key") ? "api_key" : auth === "api_key" && usable.includes("oauth2") ? "oauth2" : null;
  const inst = connector.instance;
  const key = inst?.ask === "host" || inst?.ask === "url" || inst?.ask === "base_url" ? inst.ask : null;
  return {
    address: key
      ? { key, label: ADDRESS_LABEL[key], default: inst?.default ?? "", placeholder: inst?.placeholder ?? inst?.default ?? "" }
      : null,
    auth,
    other,
    keyOptional: auth === "api_key" && usable.includes("none"),
    make_at: connector.make_at ?? null,
    needs: connector.needs ?? null,
  };
}

/** An MCP Registry vendor's own server: its address is fixed (probed, never asked); null for anything else. */
export function fixedAddress(c: Pick<CatalogueEntry, "source" | "address">): string | null {
  return c.source === "registry" && c.address ? c.address : null;
}

/** The one line under a registry vendor's name: who published it (the domain they proved they own), or, for a
 * famous vendor's server Corvus named itself, where it runs. */
export function publisherLine(c: Pick<CatalogueEntry, "source" | "publisher" | "name" | "address" | "listed_by">): string {
  if (c.source !== "registry") return "";
  if (c.listed_by === "corvus") {
    const host = c.address ? hostOf(c.address) : "";
    return host ? `${c.name}'s own server at ${host}` : "";
  }
  return c.publisher ? `Published by ${c.publisher} in the MCP Registry` : "";
}

/** A catalogue hit's second words: what it is for, its default host filled in; "" while a placeholder is left. */
export function catalogueLine(c: Pick<CatalogueEntry, "purpose" | "instance">): string {
  const text = (c.purpose ?? "").split("{host}").join(c.instance?.default ?? "{host}");
  return /\{[a-z_]+\}/.test(text) ? "" : text;
}

/** A search hit's second words: "Already connected" for a product that cannot be connected twice, else its line. */
export function hitLine(c: Pick<CatalogueEntry, "purpose" | "instance" | "connectable">): string {
  return c.connectable === false ? "Already connected" : catalogueLine(c);
}

/** Where the key is made, the host filled in ("https://{host}/-/…" with the typed or default host); null without one. */
export function makeAtFor(form: Pick<ConnectorForm, "make_at" | "address">, typed: string): string | null {
  if (!form.make_at) return null;
  if (!form.make_at.includes("{host}")) return form.make_at;
  const host = hostOf(typed.trim() || form.address?.default || "");
  return host ? form.make_at.split("{host}").join(host) : null;
}

/** The key field's label for a connector (its words, or where the key is made, say what kind it takes). */
export function keyLabel(connector: Pick<CatalogueEntry, "needs" | "make_at">): string {
  return /token/i.test(`${connector.needs ?? ""} ${connector.make_at ?? ""}`) ? "Access token" : "API key";
}

/** The switch between the two ways in, when both are offered: "Use an access token instead" / "Sign in to GitLab instead". */
export function switchLabel(other: AuthKind | null, connector: Pick<CatalogueEntry, "name" | "needs" | "make_at">): string {
  if (other === "oauth2") return `Sign in to ${connector.name} instead`;
  if (other === "api_key") return keyLabel(connector) === "Access token" ? "Use an access token instead" : "Use an API key instead";
  return "";
}

/** A host named in a refusal's words ("10.1.1.1 is private: list it first"). */
export function hostFromMessage(message: string): string | null {
  const m = message.match(/\b((?:[a-z0-9-]+\.)+[a-z]{2,}|\d{1,3}(?:\.\d{1,3}){3})(?::\d+)?\b/i);
  return m ? m[1] : null;
}

/** A refusal in one line, in the operator's words (`field`: the address the form asked for). */
export function errorLine(code: string | null, message: string, field: AddressKey | null = null): string {
  const host = hostFromMessage(message);
  switch (code) {
    case "host_not_allowed":
      return host ? `Allow ${host} first: type it in Add on the Connections page.` : "Allow the host first: type it in Add on the Connections page.";
    case "host_refused_pod":
      return "That address is Corvus's own and cannot be a connection.";
    case "host_on_this_server":
      return host ? `${host} runs on this server: allow it to connect (https only).` : "That runs on this server: allow it to connect (https only).";
    case "https_required":
      return "A public address must be https.";
    case "host_unresolved":
      return host ? `${host} does not resolve: check the spelling.` : "The host does not resolve: check the spelling.";
    case "platform_secret":
      return "That is Corvus's own token, not the product's key.";
    case "id_taken":
    case "name_taken":
      // Only the same product at the same address is "already connected" (the server says
      // "… is connected already as 'x'"); any other clash is a name, said in the server's words.
      if (!message || /connected already as /.test(message)) return "That is already connected.";
      return message;
    case "probe_failed":
      return message ? `It did not work: ${message}` : "It did not work: the product refused the key or did not answer.";
    case "needs_sign_in":
      return "Sign in to finish connecting it.";
    case "unknown_connector":
      return "Not in the catalogue: type \"mcp\" or \"api key\" to connect any server or API.";
    case "key_not_given":
      return "Paste the key.";
    case "bad_secret":
      return message || "That does not look like this product's key.";
    case "bad_instance":
      if (message) return message;
      return field === "host" ? "Give the host as a bare name, like gitlab.example.com." : "Give the full address, starting with https://.";
    case "upstream_unreachable":
      return "The product did not answer: try again in a minute.";
    case "connections_disabled":
      return "Connections are not on for this workspace.";
    case "same_key":
      return "That is the key it already has.";
    case "other_account":
      return "That key belongs to another account: the old key still works.";
    case "spec_unreadable":
    case "not_a_spec":
      return message || "That address is not an OpenAPI description Corvus can read.";
    case "spec_too_large":
      return "That description is too large to read (3 MB at most).";
    case "busy":
    case "over_limit":
      return "Corvus is busy with others like it: try again in a minute.";
    case "draft_running":
      return "A draft is already being made: wait for it.";
    case "not_editable":
      return "This connection's tools come from the catalogue and cannot be edited.";
    case "bad_tool_lines":
      return "Some lines are not tools: fix the lines named below.";
    case "needs_more_access":
      return message || "It needs more access: … → Allow more access.";
    case "stdio_refused":
      return "That is a local server Corvus never runs: give its address instead.";
    case "not_recognised":
      return "Nothing in it to connect: paste an address, a config or a command.";
    case "goes_elsewhere":
      return message || "That is not a connection: add it on the Data page.";
    case "too_long":
      return "That is too long to read: paste only the part with the address and the key.";
    case "paused":
      return message || "It is paused.";
    case "byo_not_offered":
      return "This product does not take your own app: sign in, or use a key.";
    case "not_owner":
      return "Only the agent that added it can change its tools.";
    default:
      return message || "Something went wrong.";
  }
}

export type ConnectOutcome =
  | { kind: "sign_in"; url: string; id: string | null }
  | { kind: "connected"; connection: Connection }
  | { kind: "refused" }
  | { kind: "unknown" };

/** The line when a sign-in address is not one Corvus opens. */
export const SIGN_IN_REFUSED_LINE = "The sign-in address is not one Corvus can open.";

/** A sign-in address Corvus may open (signIn.ts `isHttpsUrl`), else null. */
export function signInUrl(url: string | null | undefined): string | null {
  return isHttpsUrl(url) ? url : null;
}

/**
 * The end of a wait for a sign-in: the row has left `needs_sign_in`,
 * whatever it became. `done` with the connection when it works, else the
 * line saying what happened; null while it still waits.
 */
export function signInEnded(row: Connection | undefined): { connection: Connection | null; line: string } | null {
  if (!row || row.status === "needs_sign_in") return null;
  if (row.status === "ok") return { connection: row, line: "" };
  const name = row.name || row.id;
  const why = row.status_words && row.status_words !== "signed in" ? `: ${row.status_words}` : ".";
  return { connection: null, line: `Signed in to ${name}, but it is ${statusWords(row.status).toLowerCase()}${why}` };
}

/** What a connect answered: a sign-in to open (its row waits for it), or the connection made. */
export function connectOutcome(raw: ConnectOut | null | undefined): ConnectOutcome {
  if (!raw) return { kind: "unknown" };
  const url = signInUrl(raw.authorize_url);
  if (url) return { kind: "sign_in", url, id: raw.connection?.id ?? null };
  if (raw.authorize_url) return { kind: "refused" };
  if (raw.connection) return { kind: "connected", connection: raw.connection };
  return { kind: "unknown" };
}

/** The line after a remove: what Lattice could not do itself (a key is deleted at the product). */
export function removedLine(name: string, hint: string | null | undefined): string {
  return hint ? `${name} is removed. ${hint}` : `${name} is removed.`;
}

/** Interactions on the page to connect a connector: type, [host], sign in or key, approve or Connect, [scope]. */
export function interactions(connector: CatalogueEntry, opts: { ownHost?: boolean; scopeChange?: boolean } = {}): number {
  const form = formFor(connector);
  return 1 + (form.address && opts.ownHost ? 1 : 0) + (form.auth === "none" ? 1 : 2) + (opts.scopeChange ? 1 : 0);
}

const TICKET_DEAD: Record<string, string> = {
  link_unknown: "This link is not one Corvus knows. Ask the agent for a new one.",
  link_used: "This link has already been used. Ask the agent for a new one if it is still needed.",
  link_void: "A newer link replaced this one. Ask the agent for it.",
  link_expired: "This link has expired. Ask the agent for a new one.",
};

/** The one line of a dead ticket, by its code; the unknown-link line without one. */
export function ticketDeadLine(code: string | null): string {
  return TICKET_DEAD[code ?? ""] ?? TICKET_DEAD.link_unknown;
}

/** The ticket token in a page's fragment (/connect#lat_cx_…), or null. */
export function ticketFromHash(hash: string): string | null {
  const m = hash.replace(/^#/, "").match(/^(lat_cx_[A-Za-z0-9_-]{20,})$/);
  return m ? m[1] : null;
}

/** The ticket of a /connect page: its fragment's, else the one this tab kept (a reload after the fragment was stripped). */
export function ticketFrom(hash: string, kept: string | null | undefined): string | null {
  return ticketFromHash(hash) ?? ticketFromHash(kept ?? "");
}
